import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { operationService } from '../services/operationService';
import { Operation } from '../types';
import { formatApiDateTimeForDisplay } from '../utils/dateTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFilePdf, faTrash, faLock, faLockOpen, faBoxArchive } from '@fortawesome/free-solid-svg-icons';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check permissions
  const canGenerateReports = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-reports'];
  const canManageOperations = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-operations'];
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [asmFilter, setAsmFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [operations, setOperations] = useState<Operation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [deletingOperationId, setDeletingOperationId] = useState<number | null>(null);
  const [togglingOperationId, setTogglingOperationId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    operation: Operation | null;
    isDeleting: boolean;
  }>({
    isOpen: false,
    operation: null,
    isDeleting: false
  });

  const [closeModal, setCloseModal] = useState<{
    isOpen: boolean;
    operation: Operation | null;
    isProcessing: boolean;
  }>({
    isOpen: false,
    operation: null,
    isProcessing: false
  });

  // Archive view toggle (sealed operations = archived)
  const [showArchive, setShowArchive] = useState(false);

  // Check if device is mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchOperations = async () => {
      try {
        setLoading(true);
        setError('');
        
        if (user?.orgID && user.orgID > 0) {
          const data = await operationService.getOperationsByYear([user.orgID], selectedYear);
          setOperations(data);
        } else {
          setError('Keine Organisations-ID verfügbar');
        }
      } catch (error: any) {
        setError(error.message || 'Fehler beim Laden der Einsätze');
        console.error('Error fetching operations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOperations();
  }, [user?.orgID, selectedYear]);

  const archivedCount = operations.filter(op => op.lockStatus === 2).length;

  const filteredOperations = operations.filter((op) => {
    // Sealed operations (lockStatus === 2) go to archive
    if (showArchive && op.lockStatus !== 2) return false;
    if (!showArchive && op.lockStatus === 2) return false;

    const matchesSearch = (op.title || `${op.location}: ${op.alertTitle}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         op.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAsm = asmFilter === 'all' || 
                      (asmFilter === 'yes' && op.asm_teams === true) ||
                      (asmFilter === 'no' && op.asm_teams !== true);
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'open' && op.lockStatus === 0) ||
                         (statusFilter === 'closed' && op.lockStatus === 1);
    
    // In archive view, skip status filter (all are sealed)
    return matchesSearch && matchesAsm && (showArchive || matchesStatus);
  });

  const handleViewOperation = (operation: Operation) => {
    navigate(`/operation/${operation.id}`);
  };



  const handleGenerateReport = async (operation: Operation) => {
    // Check if this is an ASM operation
    if (!operation.asm_teams) {
      alert('PDF Export ist nur für Atemschutzeinsätze verfügbar.');
      return;
    }

    try {
      if (user?.orgID) {
        const blob = await operationService.generateReport(user.orgID, operation.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const einsatzName = operation.title || `${operation.location}: ${operation.alertTitle}` || 'Unbekannter_Einsatz';
        a.download = `Einsatzbericht_${einsatzName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error: any) {
      alert(`Fehler beim Generieren des PDF-Berichts: ${error.message}`);
    }
  };

  const handleDeleteOperation = (operation: Operation) => {
    // Check if smsTxtOrig is filled
    if (operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '') {
      alert('Externe Einsätze (aus einem Fremdsystem) können nicht gelöscht werden.');
      return;
    }

    // Open delete modal
    setDeleteModal({
      isOpen: true,
      operation,
      isDeleting: false
    });
  };

  const confirmDeleteOperation = async () => {
    if (!deleteModal.operation || !user?.orgID) return;

    try {
      setDeleteModal(prev => ({ ...prev, isDeleting: true }));
      setDeletingOperationId(deleteModal.operation.id);
      
      await operationService.deleteOperation(user.orgID, deleteModal.operation.id);
      
      // Remove operation from local state
      setOperations(prev => prev.filter(op => op.id !== deleteModal.operation!.id));
      
      // Close modal after successful deletion
      setDeleteModal({
        isOpen: false,
        operation: null,
        isDeleting: false
      });
      setDeletingOperationId(null);
    } catch (error: any) {
      alert(`Fehler beim Löschen des Einsatzes: ${error.message}`);
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
      setDeletingOperationId(null);
    }
  };

  const cancelDeleteOperation = () => {
    setDeleteModal({
      isOpen: false,
      operation: null,
      isDeleting: false
    });
    setDeletingOperationId(null);
  };

  const handleToggleOperationStatus = async (operation: Operation) => {
    if (!user?.orgID) return;

    if (operation.lockStatus === 1) {
      // Operation is closed, open it
      try {
        setTogglingOperationId(operation.id);
        await operationService.openOperation(user.orgID, operation.id);
        // Update local state
        setOperations(prev => prev.map(op => 
          op.id === operation.id 
            ? { ...op, lockStatus: 0 }
            : op
        ));
      } catch (error: any) {
        alert(`Fehler beim Öffnen des Einsatzes: ${error.message}`);
      } finally {
        setTogglingOperationId(null);
      }
    } else {
      // Operation is open, show close/seal dialog
      setCloseModal({
        isOpen: true,
        operation: operation,
        isProcessing: false
      });
    }
  };

  const handleCloseOperation = async () => {
    if (!closeModal.operation || !user?.orgID) return;

    try {
      setCloseModal(prev => ({ ...prev, isProcessing: true }));
      await operationService.closeOperation(user.orgID, closeModal.operation.id);
      
      // Update local state
      setOperations(prev => prev.map(op => 
        op.id === closeModal.operation!.id 
          ? { ...op, lockStatus: 1 }
          : op
      ));
      
      setCloseModal({ isOpen: false, operation: null, isProcessing: false });
    } catch (error: any) {
      alert(`Fehler beim Schliessen des Einsatzes: ${error.message}`);
      setCloseModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleSealOperation = async () => {
    if (!closeModal.operation || !user?.orgID) return;

    try {
      setCloseModal(prev => ({ ...prev, isProcessing: true }));
      await operationService.sealOperation(user.orgID, closeModal.operation.id);
      
      // Update local state
      setOperations(prev => prev.map(op => 
        op.id === closeModal.operation!.id 
          ? { ...op, lockStatus: 2 }
          : op
      ));
      
      setCloseModal({ isOpen: false, operation: null, isProcessing: false });
    } catch (error: any) {
      alert(`Fehler beim Versiegeln des Einsatzes: ${error.message}`);
      setCloseModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const cancelCloseOperation = () => {
    setCloseModal({ isOpen: false, operation: null, isProcessing: false });
  };

  const formatDate = (dateString: string) => {
    return formatApiDateTimeForDisplay(dateString);
  };

  if (loading) {
    return <div className="portal-loading">Einsätze werden geladen…</div>;
  }

  if (error) {
    return (
      <div className="portal-error">
        <strong>Fehler:</strong> {error}
      </div>
    );
  }

  return (
    <div className="portal-stack">
      <div className="portal-page-header">
        <div>
          <h1 className="portal-page-title">{showArchive ? 'Archiv' : 'Einsätze'}</h1>
          <p className="portal-page-subtitle">
            {showArchive
              ? 'Versiegelte Einsätze zur Nachvollziehbarkeit.'
              : 'Aktive und geschlossene Einsätze deiner Organisation.'}
          </p>
        </div>
        <div className="portal-seg">
          <button
            type="button"
            className={`portal-seg-btn${!showArchive ? ' is-active' : ''}`}
            onClick={() => setShowArchive(false)}
          >
            Einsätze ({operations.length - archivedCount})
          </button>
          <button
            type="button"
            className={`portal-seg-btn${showArchive ? ' is-active' : ''}`}
            onClick={() => setShowArchive(true)}
          >
            <FontAwesomeIcon icon={faBoxArchive} />
            Archiv ({archivedCount})
          </button>
        </div>
      </div>

      <div className="portal-card">
        <div className="portal-filters">
          <div className="portal-filter-field portal-filter-field--grow">
            <label className="portal-label">Suchen</label>
            <input
              type="text"
              className="portal-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nach Einsatz suchen…"
            />
          </div>
          <div className="portal-filter-field">
            <label className="portal-label">Jahr</label>
            <select
              className="portal-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="portal-filter-field">
            <label className="portal-label">Atemschutz</label>
            <select className="portal-select" value={asmFilter} onChange={(e) => setAsmFilter(e.target.value)}>
              <option value="all">Alle</option>
              <option value="yes">Mit Atemschutz</option>
              <option value="no">Ohne Atemschutz</option>
            </select>
          </div>
          {!showArchive && (
            <div className="portal-filter-field">
              <label className="portal-label">Status</label>
              <select className="portal-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">Alle</option>
                <option value="open">Geöffnet</option>
                <option value="closed">Geschlossen</option>
              </select>
            </div>
          )}
          <div className="portal-hint" style={{ paddingBottom: 10 }}>
            {filteredOperations.length} von {operations.length} Einsätzen
          </div>
        </div>
      </div>

      <div className="portal-table-wrap">
        <table className="portal-table">
          <thead>
            <tr>
              {!isMobile && <th style={{ width: 80 }}>Nummer</th>}
              <th>Titel</th>
              {!isMobile && (
                <>
                  <th>Kategorie</th>
                  <th>Atemschutz</th>
                  <th>Ort</th>
                  <th>Erstellt</th>
                </>
              )}
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredOperations.map((operation) => (
              <tr
                key={operation.id}
                className={(operation.lockStatus === 1 || operation.lockStatus === 2) ? 'is-muted' : undefined}
              >
                {!isMobile && <td>{operation.opsNumber}</td>}
                <td>
                  <div style={{ fontWeight: 700 }}>{operation.title || `${operation.location}: ${operation.alertTitle}`}</div>
                  {isMobile && <div className="portal-hint" style={{ marginTop: 4 }}>{operation.location}</div>}
                </td>
                {!isMobile && (
                  <>
                    <td>{operation.category}</td>
                    <td>
                      <span className={`portal-badge ${operation.asm_teams ? 'portal-badge--solid-success' : 'portal-badge--solid-danger'}`}>
                        {operation.asm_teams ? 'Ja' : 'Nein'}
                      </span>
                    </td>
                    <td>{operation.location}</td>
                    <td>{formatDate(operation.created)}</td>
                  </>
                )}
                <td>
                  <div className="portal-btn-group">
                    <button type="button" className="portal-btn portal-btn--primary portal-btn--icon portal-btn--sm" onClick={() => handleViewOperation(operation)} title="Details anzeigen">
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    {canGenerateReports && (
                      <button
                        type="button"
                        className="portal-btn portal-btn--primary portal-btn--icon portal-btn--sm"
                        onClick={() => handleGenerateReport(operation)}
                        disabled={!operation.asm_teams}
                        title={operation.asm_teams ? 'PDF Bericht generieren' : 'PDF Export nur für Atemschutzeinsätze verfügbar'}
                      >
                        <FontAwesomeIcon icon={faFilePdf} />
                      </button>
                    )}
                    {canManageOperations && (
                      <button
                        type="button"
                        className="portal-btn portal-btn--icon portal-btn--sm"
                        onClick={() => handleToggleOperationStatus(operation)}
                        disabled={togglingOperationId === operation.id || operation.lockStatus === 2}
                        style={{
                          background:
                            togglingOperationId === operation.id
                              ? '#666'
                              : operation.lockStatus === 2
                                ? '#4a148c'
                                : operation.lockStatus === 1
                                  ? '#ff9800'
                                  : '#2e7d32',
                          color: '#fff',
                        }}
                        title={
                          togglingOperationId === operation.id
                            ? 'Wird geändert…'
                            : operation.lockStatus === 2
                              ? 'Einsatz ist versiegelt'
                              : operation.lockStatus === 1
                                ? 'Einsatz öffnen'
                                : 'Einsatz schliessen'
                        }
                      >
                        <FontAwesomeIcon icon={operation.lockStatus === 1 ? faLockOpen : faLock} />
                      </button>
                    )}
                    {canManageOperations && !showArchive && (
                      <button
                        type="button"
                        className="portal-btn portal-btn--danger portal-btn--icon portal-btn--sm"
                        onClick={() => handleDeleteOperation(operation)}
                        disabled={
                          deletingOperationId === operation.id ||
                          Boolean(operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '') ||
                          operation.lockStatus === 2
                        }
                        title={
                          deletingOperationId === operation.id
                            ? 'Wird gelöscht…'
                            : operation.lockStatus === 2
                              ? 'Versiegelte Einsätze können nicht gelöscht werden'
                              : (operation.smsTxtOrig && operation.smsTxtOrig.trim() !== '')
                                ? 'Externe Einsätze können nicht gelöscht werden'
                                : 'Einsatz löschen'
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOperations.length === 0 && !loading && (
        <div className="portal-card portal-empty">
          {showArchive
            ? (archivedCount === 0 ? 'Keine versiegelten Einsätze im Archiv' : 'Keine archivierten Einsätze gefunden')
            : (operations.length === 0 ? 'Keine Einsätze verfügbar' : 'Keine Einsätze gefunden')}
        </div>
      )}

      {deleteModal.isOpen && (
        <div className="portal-modal-backdrop">
          <div className="portal-modal" style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: 'var(--portal-red-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--portal-red)',
            }}>
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: 22 }} />
            </div>
            <h2 className="portal-modal-title">Einsatz löschen</h2>
            <p className="portal-page-subtitle" style={{ margin: '0 auto 12px' }}>
              Möchten Sie den Einsatz <strong>"{deleteModal.operation?.title || `${deleteModal.operation?.location}: ${deleteModal.operation?.alertTitle}` || 'Unbekannter Einsatz'}"</strong> wirklich löschen?
            </p>
            <p className="portal-hint">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="portal-modal-actions" style={{ justifyContent: 'center' }}>
              <button type="button" className="portal-btn portal-btn--secondary" onClick={cancelDeleteOperation} disabled={deleteModal.isDeleting}>Abbrechen</button>
              <button type="button" className="portal-btn portal-btn--danger" onClick={confirmDeleteOperation} disabled={deleteModal.isDeleting}>
                {deleteModal.isDeleting ? 'Wird gelöscht…' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {closeModal.isOpen && closeModal.operation && (
        <div className="portal-modal-backdrop">
          <div className="portal-modal">
            <h2 className="portal-modal-title" style={{ textAlign: 'center' }}>Einsatz schliessen</h2>
            <p className="portal-page-subtitle" style={{ textAlign: 'center', margin: '0 auto 18px' }}>
              Wie möchtest Du den Einsatz <strong>"{closeModal.operation.title || `${closeModal.operation.location}: ${closeModal.operation.alertTitle}` || 'Unbekannter Einsatz'}"</strong> behandeln?
            </p>
            <div className="portal-stack" style={{ gap: 10, marginBottom: 8 }}>
              <div className="portal-card" style={{ padding: 14 }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>🔒 Schliessen</div>
                <div className="portal-hint">Einsatz wird geschlossen, kann aber später noch bearbeitet werden.</div>
              </div>
              <div className="portal-card" style={{ padding: 14, borderColor: '#ffc107', background: '#fffbf0' }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>🔐 Versiegeln</div>
                <div className="portal-hint">Dauerhaft versiegelt und ins Archiv verschoben — keine Bearbeitung mehr.</div>
              </div>
            </div>
            <div className="portal-modal-actions" style={{ justifyContent: 'center' }}>
              <button type="button" className="portal-btn portal-btn--secondary" onClick={cancelCloseOperation} disabled={closeModal.isProcessing}>Abbrechen</button>
              <button type="button" className="portal-btn portal-btn--success" onClick={handleCloseOperation} disabled={closeModal.isProcessing}>
                {closeModal.isProcessing ? 'Wird geschlossen…' : 'Schliessen'}
              </button>
              <button
                type="button"
                className="portal-btn"
                onClick={handleSealOperation}
                disabled={closeModal.isProcessing}
                style={{ background: '#ffc107', color: '#333' }}
              >
                {closeModal.isProcessing ? 'Wird versiegelt…' : 'Versiegeln'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default Dashboard; 